import { paymentMode } from '@ourbranch/lookups';

import { get, policyTypes } from 'core/helpers/lookups';
import { getPolicyStatus, PolicyStatus, PolicyStatusLabel } from 'core/helpers/policy-status';

const getPolicyTypeName = get(policyTypes);

// The policies the Overview table lists under "Active": everything not yet expired or cancelled.
const isOpenPolicy = (policy) => ![PolicyStatus.Inactive, PolicyStatus.Cancelled].includes(getPolicyStatus(policy));

export const getOpenPolicies = (policies = []) => policies.filter(isOpenPolicy);

// The customer's status is the most current status across their policies.
const STATUS_PRECEDENCE = [
  PolicyStatus.Active,
  PolicyStatus.InCancellation,
  PolicyStatus.Future,
  PolicyStatus.Cancelled,
  PolicyStatus.Inactive
];

export const getCustomerStatus = (policies = []) => {
  const statuses = new Set(policies.map((policy) => getPolicyStatus(policy)));
  return STATUS_PRECEDENCE.find((status) => statuses.has(status)) ?? null;
};

export const getCustomerStatusLabel = (status) => PolicyStatusLabel[status] ?? null;

export const getPolicyTabLabel = (policy) => `${getPolicyTypeName(policy.policyType) ?? 'Unknown'} policy`;

// "Home & auto bundle" when the open policies cover more than one line.
export const getBundleLabel = (openPolicies = []) => {
  const names = [...new Set(openPolicies.map((policy) => getPolicyTypeName(policy.policyType)).filter(Boolean))];
  if (names.length < 2) {
    return null;
  }
  const [first, ...rest] = names;
  return `${[first, ...rest.map((name) => name.toLowerCase())].join(' & ')} bundle`;
};

export const isAutopayOn = (openPolicies = []) =>
  openPolicies.length > 0 && openPolicies.every((policy) => policy.paymentMode === paymentMode.Automated);

export const getPolicyAffinity = (policy) => policy?.segments?.[0]?.global?.affinity ?? null;

// One chip when the member and policy codes match, one per code when they differ.
export const getAffinityChips = ({ memberAffinityCode, policyAffinityCode }) => {
  if (memberAffinityCode && memberAffinityCode === policyAffinityCode) {
    return [`Affinity code ${memberAffinityCode}`];
  }
  return [
    memberAffinityCode && `Member affinity ${memberAffinityCode}`,
    policyAffinityCode && `Policy affinity ${policyAffinityCode}`
  ].filter(Boolean);
};

export const getInitials = ({ fname, lname }) => `${fname?.[0] ?? ''}${lname?.[0] ?? ''}`.toUpperCase();

export const WorkspaceSections = {
  OVERVIEW: 'overview',
  BILLING: 'billing',
  DOCUMENTS: 'documents',
  INTERACTIONS: 'interactions',
  CLAIMS: 'claims'
};

// The workspace tab row: Overview, one tab per open policy (plus the policy being viewed when it is
// expired or cancelled, labeled with its status), household Billing when there's an open policy, then the
// account-level tabs.
export const getWorkspaceTabs = ({
  accountId,
  policies = [],
  currentPolicyId,
  showBilling,
  showInteractionsAndClaims
}) => {
  const policyTabs = policies.filter((policy) => policy.id === currentPolicyId || isOpenPolicy(policy));
  const labelCounts = policyTabs.reduce((counts, policy) => {
    const label = getPolicyTabLabel(policy);
    return { ...counts, [label]: (counts[label] ?? 0) + 1 };
  }, {});
  const base = `/customer/${accountId}`;
  return [
    { value: WorkspaceSections.OVERVIEW, label: 'Overview', to: base },
    ...policyTabs.map((policy) => {
      const label = getPolicyTabLabel(policy);
      return {
        value: policy.id,
        // Two tabs for the same line (a term and its renewal) are told apart by status, and a closed one says so.
        label:
          labelCounts[label] > 1 || !isOpenPolicy(policy)
            ? `${label} (${PolicyStatusLabel[getPolicyStatus(policy)]})`
            : label,
        to: `${base}/policy/${policy.id}`,
        title: policy.id
      };
    }),
    ...(showBilling && policies.some(isOpenPolicy)
      ? [{ value: WorkspaceSections.BILLING, label: 'Billing', to: `${base}/${WorkspaceSections.BILLING}` }]
      : []),
    { value: WorkspaceSections.DOCUMENTS, label: 'Documents', to: `${base}/${WorkspaceSections.DOCUMENTS}` },
    ...(showInteractionsAndClaims
      ? [
          {
            value: WorkspaceSections.INTERACTIONS,
            label: 'Interactions',
            to: `${base}/${WorkspaceSections.INTERACTIONS}`
          },
          { value: WorkspaceSections.CLAIMS, label: 'Claims', to: `${base}/${WorkspaceSections.CLAIMS}` }
        ]
      : [])
  ];
};
